/**
 * PhysicsSubsystem — 2D physics for the player runtime.
 *
 * On level load: creates a physics world, scans all GameObjects for
 * RigidBody2D / collider / zone components and populates the world.
 * On each update: steps the engine and writes body transforms back.
 *
 * Uses Rapier2D (WASM) by default, falling back to the built-in solver.
 *
 * Zero editor dependencies.
 *
 * @module @engine/player
 */

import {
  Subsystem,
  SubsystemPriority,
  type SubsystemConfig,
  type Level,
  type GameObject,
  type BoxCollider2D,
  type CircleCollider2D,
  type CapsuleCollider2D,
  type PolygonCollider2D,
  type ConvexCollider2D,
} from '@engine/core';
import Logger from '@engine/logger';
import {
  BuiltinPhysicsEngine,
  PhysicsBodyType,
  type IPhysicsEngine2D,
  type PhysicsWorldOptions,
  type PhysicsBodyHandle,
  type PhysicsBodyEventHandlers,
  type RigidBodyDescriptor,
  type ColliderDescriptor,
  type ColliderShape,
  type PhysVec2,
  type GravityZoneDescriptor,
  type BuoyancyZoneDescriptor,
  type WindZoneDescriptor,
} from '@engine/physics2d';
import type { ScriptSubsystem } from './ScriptSubsystem';

// ============================================================================
// HELPERS
// ============================================================================

type ColliderData = BoxCollider2D | CircleCollider2D | CapsuleCollider2D | PolygonCollider2D | ConvexCollider2D;

function toBodyType(v: unknown): PhysicsBodyType {
  const n = Number(v) | 0;
  if (n === 1) return PhysicsBodyType.Kinematic;
  if (n === 2) return PhysicsBodyType.Static;
  return PhysicsBodyType.Dynamic;
}

// ============================================================================
// OPTIONS
// ============================================================================

export interface PhysicsSubsystemOptions {
  /** Which backend to use. Default: 'rapier2d' (WASM). Falls back to builtin. */
  engine?: 'rapier2d' | 'builtin';
  /** PhysicsWorld configuration (gravity, etc.) */
  worldConfig?: PhysicsWorldOptions;
  /** Custom URL for the Rapier2D WASM file (for HTML5 export). */
  wasmUrl?: string;
}

// ============================================================================
// SUBSYSTEM
// ============================================================================


const log = Logger.create('Physics');

export class PhysicsSubsystem extends Subsystem {
  readonly config: SubsystemConfig = {
    name: 'physics',
    priority: SubsystemPriority.Runtime + 5,
    optional: true,
  };

  private _engine: IPhysicsEngine2D;
  private _worldConfig: PhysicsWorldOptions;
  private _requestedBackend: 'rapier2d' | 'builtin';
  private _wasmUrl?: string;

  /** GUID → body handle */
  private _guidToBody = new Map<string, PhysicsBodyHandle>();

  /** body handle → GUID (reverse lookup) */
  private _bodyToGuid = new Map<PhysicsBodyHandle, string>();

  /** GUID → numeric GameObject.id (for entity references in scripts) */
  private _guidToId = new Map<string, number>();

  /** Currently active level */
  private _level: Level | null = null;

  /** SceneEvent unsubscribe functions */
  private _eventUnsubs: (() => void)[] = [];

  /** Set once stepping throws (e.g. Rapier WASM trap in the .NET player) — stops retrying. */
  private _stepFailed = false;

  constructor(options?: PhysicsSubsystemOptions) {
    super();
    this._requestedBackend = options?.engine ?? 'rapier2d';
    this._worldConfig = options?.worldConfig ?? {};
    this._wasmUrl = options?.wasmUrl;
    // Start with builtin; upgrade to rapier2d async in onInitialize
    this._engine = new BuiltinPhysicsEngine();
  }

  /** The underlying physics engine */
  get engine(): IPhysicsEngine2D {
    return this._engine;
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  async onInitialize(): Promise<void> {
    // NOTE: Rapier is pinned to 0.17.3 repo-wide (catalog). 0.18.0+ trips a WASM codegen bug in
    // the .NET player's ClearScript V8 — `step` traps "RuntimeError: unreachable" on the first
    // call (verified headlessly: works in browser/Node V8, both SIMD and non-SIMD 0.18+ builds
    // trap, 0.17.3 steps fine). 0.17.3 runs everywhere, so keep one Rapier version for all targets.
    if (this._requestedBackend === 'rapier2d') {
      try {
        const { RapierPhysicsEngine } = await import('@engine/physics2d-rapier2d');
        const rapier = new RapierPhysicsEngine();
        await rapier.init(this._wasmUrl);
        this._engine = rapier;
        log.info('[PhysicsSubsystem] Using Rapier2D (WASM)');
        return;
      } catch (err) {
        // Fall back to the builtin solver rather than disabling physics entirely.
        log.warn('[PhysicsSubsystem] Rapier2D init failed, falling back to builtin:', err);
      }
    }

    this._engine = new BuiltinPhysicsEngine();
    await this._engine.init();
    log.info('[PhysicsSubsystem] Using builtin physics');
  }

  onUpdate(dt: number): void {
    if (!this._level || this._stepFailed) return;

    try {
      // Step physics (engine has its own fixed-timestep accumulator)
      this._engine.step(dt);
      // Write transforms back
      this._syncBodiesToTransforms();
    } catch (err) {
      // Rapier's WASM step traps ("unreachable") in the .NET ClearScript player
      // (works in the browser). Disable physics for this session rather than
      // trap every frame — which would also skip later update-phase subsystems.
      this._stepFailed = true;
      log.error('[PhysicsSubsystem] step failed — physics disabled for this session:', err);
    }
  }

  onShutdown(): void {
    this._destroyWorld();
    this._engine.dispose();
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  /**
   * Set the active level — creates the physics world and populates it.
   */
  setLevel(level: Level | null): void {
    // Tear down previous world
    this._destroyWorld();
    this._level = level;

    if (!level) return;

    // Create fresh world
    this._engine.createWorld(this._worldConfig);

    // Scan all GameObjects
    for (const go of level.getAllObjects()) {
      this._createPhysicsBody(go);
      this._registerGravityZone(go);
      this._registerBuoyancyZone(go);
      this._registerWindZone(go);
    }

    // Subscribe to runtime scene changes
    this._eventUnsubs.push(
      level.events.onObjectAdded(({ gameObject }) => {
        this._createPhysicsBody(gameObject);
        this._registerGravityZone(gameObject);
        this._registerBuoyancyZone(gameObject);
        this._registerWindZone(gameObject);
      }),
      level.events.onObjectRemoved(({ gameObject }) => {
        this._removePhysicsBody(gameObject.guid);
      }),
    );

    log.info(`[PhysicsSubsystem] World created with ${this._guidToBody.size} bodies`);
  }

  // ==========================================================================
  // INTERNAL — world management
  // ==========================================================================

  private _destroyWorld(): void {
    // Unsubscribe from scene events
    for (const unsub of this._eventUnsubs) unsub();
    this._eventUnsubs = [];

    if (this._guidToBody.size > 0) {
      this._engine.destroyWorld();
      this._guidToBody.clear();
      this._bodyToGuid.clear();
      this._guidToId.clear();
    }
  }

  private _removePhysicsBody(guid: string): void {
    const handle = this._guidToBody.get(guid);
    if (handle != null) {
      this._engine.removeBody(handle);
      this._guidToBody.delete(guid);
      this._bodyToGuid.delete(handle);
      this._guidToId.delete(guid);
    }
  }

  // ==========================================================================
  // INTERNAL — scene → physics
  // ==========================================================================

  private _createPhysicsBody(go: GameObject): void {
    const rb = go.getComponentData('core.RigidBody2D');

    const hasCollider =
      go.getComponent('core.BoxCollider2D') !== null ||
      go.getComponent('core.CircleCollider2D') !== null ||
      go.getComponent('core.CapsuleCollider2D') !== null ||
      go.getComponent('core.PolygonCollider2D') !== null ||
      go.getComponent('core.ConvexCollider2D') !== null;

    if (rb === null && !hasCollider) return;

    const bodyType = rb ? toBodyType(rb.bodyType) : PhysicsBodyType.Static;
    const pos: PhysVec2 = {
      x: go.transform.position.x,
      y: go.transform.position.y,
    };

    let constraints = 0;
    if (rb?.freezePositionX) constraints |= 1;
    if (rb?.freezePositionY) constraints |= 2;
    if (rb?.freezeRotation) constraints |= 4;

    const desc: RigidBodyDescriptor = {
      guid: go.guid,
      gameObjectId: go.id,
      bodyType,
      position: pos,
      rotation: -go.transform.rotationRadians,
      mass: rb?.mass || undefined,
      linearDamping: rb?.linearDamping ?? 0,
      angularDamping: rb?.angularDamping ?? 0.05,
      gravityScale: rb?.gravityScale ?? 1,
      constraints,
    };

    const bodyHandle = this._engine.createBody(desc);
    this._guidToBody.set(go.guid, bodyHandle);
    this._bodyToGuid.set(bodyHandle, go.guid);
    this._guidToId.set(go.guid, go.id);

    // Attach colliders
    const boxData = go.getComponentData('core.BoxCollider2D');
    if (boxData) this._attachCollider(go, bodyHandle, boxData, 'box');
    const circleData = go.getComponentData('core.CircleCollider2D');
    if (circleData) this._attachCollider(go, bodyHandle, circleData, 'circle');
    const capsuleData = go.getComponentData('core.CapsuleCollider2D');
    if (capsuleData) this._attachCollider(go, bodyHandle, capsuleData, 'capsule');
    const polyData = go.getComponentData('core.PolygonCollider2D');
    if (polyData) this._attachCollider(go, bodyHandle, polyData, 'polygon');
    const convexData = go.getComponentData('core.ConvexCollider2D');
    if (convexData) this._attachCollider(go, bodyHandle, convexData, 'convex');

    // Register collision/trigger event handlers for scripting
    this._registerEventHandlers(go, bodyHandle);
  }

  private _attachCollider(
    go: GameObject,
    bodyHandle: PhysicsBodyHandle,
    d: ColliderData,
    shapeType: 'box' | 'circle' | 'capsule' | 'polygon' | 'convex',
  ): void {
    const sx = Math.abs(go.transform.lossyScale?.x ?? 1);
    const sy = Math.abs(go.transform.lossyScale?.y ?? 1);

    const { offset, isTrigger, friction, bounciness, density, frictionCombine, bouncinessCombine } = d;

    let shape: ColliderShape;

    switch (shapeType) {
      case 'box': {
        const { size } = d as BoxCollider2D;
        shape = { type: 'box', halfExtents: { x: size.x * sx * 0.5, y: size.y * sy * 0.5 } };
        break;
      }
      case 'circle': {
        const { radius } = d as CircleCollider2D;
        shape = { type: 'circle', radius: radius * Math.max(sx, sy) };
        break;
      }
      case 'capsule': {
        const { size } = d as CapsuleCollider2D;
        const scaledW = size.x * sx;
        const scaledH = size.y * sy;
        const capsuleRadius = Math.min(scaledW, scaledH) * 0.5;
        const totalHeight = Math.max(scaledW, scaledH);
        const halfHeight = Math.max(0, (totalHeight - capsuleRadius * 2) * 0.5);
        shape = { type: 'capsule', halfHeight, radius: capsuleRadius };
        break;
      }
      case 'polygon': {
        const { points } = d as PolygonCollider2D;
        // Skip if no valid polygon
        if (!points || points.length < 3) return;
        shape = { type: 'polygon', vertices: points.map(p => ({ x: p.x * sx, y: p.y * sy })) };
        break;
      }
      case 'convex': {
        const convex = d as ConvexCollider2D;
        const { points, detectedShape, boxHalfExtents, circleRadius } = convex;
        
        // Use detected optimal shape if available
        if (detectedShape === 'box' && boxHalfExtents) {
          shape = { type: 'box', halfExtents: { x: boxHalfExtents.x * sx, y: boxHalfExtents.y * sy } };
        } else if (detectedShape === 'circle' && circleRadius) {
          // Use average scale for circle radius
          const avgScale = (sx + sy) * 0.5;
          shape = { type: 'circle', radius: circleRadius * avgScale };
        } else {
          // Fall back to polygon
          if (!points || points.length < 3) return;
          shape = { type: 'polygon', vertices: points.map((p: { x: number; y: number }) => ({ x: p.x * sx, y: p.y * sy })) };
        }
        break;
      }
    }

    const desc: ColliderDescriptor = {
      shape,
      offset: { x: offset.x * sx, y: offset.y * sy },
      isTrigger,
      material: { friction, bounciness, density, frictionCombine, bouncinessCombine },
      oneWay: (d as { oneWay?: boolean }).oneWay,
      oneWayDirection: (d as { oneWayDirection?: { x: number; y: number } }).oneWayDirection,
    };

    this._engine.addCollider(bodyHandle, desc);
  }

  // ==========================================================================
  // SCRIPTING — GUID-addressed queries & forces
  // ==========================================================================
  // Consumed by `engine.physics` through `makePhysicsBacking` (DI per runtime, §13.4c).
  // Structurally mirrors the editor PhysicsSubsystem so the same adapter serves both runtimes.

  raycast(ox: number, oy: number, dx: number, dy: number, maxDist: number, layerMask?: number) {
    const hit = this._engine.raycast({ x: ox, y: oy }, { x: dx, y: dy }, maxDist, layerMask);
    if (!hit) return null;
    return {
      point: hit.point,
      normal: hit.normal,
      distance: hit.distance,
      bodyGuid: this._bodyToGuid.get(hit.bodyHandle),
    };
  }

  overlapCircle(cx: number, cy: number, radius: number, layerMask?: number): string[] {
    return this._engine
      .overlapCircle({ x: cx, y: cy }, radius, layerMask)
      .map((h) => this._bodyToGuid.get(h))
      .filter((g): g is string => g != null);
  }

  applyForce(guid: string, fx: number, fy: number): void {
    const handle = this._guidToBody.get(guid);
    if (handle != null) this._engine.applyForce(handle, { x: fx, y: fy });
  }

  applyImpulse(guid: string, ix: number, iy: number): void {
    const handle = this._guidToBody.get(guid);
    if (handle != null) this._engine.applyImpulse(handle, { x: ix, y: iy });
  }

  setLinearVelocity(guid: string, vx: number, vy: number): void {
    const handle = this._guidToBody.get(guid);
    if (handle != null) this._engine.setLinearVelocity(handle, { x: vx, y: vy });
  }

  getLinearVelocity(guid: string): { x: number; y: number } {
    const handle = this._guidToBody.get(guid);
    return handle != null ? this._engine.getLinearVelocity(handle) : { x: 0, y: 0 };
  }

  // ==========================================================================
  // INTERNAL — collision/trigger event handlers
  // ==========================================================================

  private _registerEventHandlers(go: GameObject, bodyHandle: PhysicsBodyHandle): void {
    // Wire only if the object runs a core.Script that can react to collisions.
    if (!go.getComponentData('core.Script')) return;

    let script: ScriptSubsystem | undefined;
    try { script = this.getSubsystem<ScriptSubsystem>('script'); } catch { /* not registered */ }

    const guid = go.guid;

    // The canonical event id (`events.*`) maps to `on*` hooks in TypeScript behaviours and is
    // consumed directly by the Redprint backend; `other` is the real `go.id` (player _guidToId).
    const dispatch = (event: string, otherGuid: string, contact: Record<string, unknown>): void => {
      script?.triggerEvent(guid, event, { other: this._guidToId.get(otherGuid) ?? -1, ...contact });
    };

    const handlers: PhysicsBodyEventHandlers = {
      onCollisionEnter: (otherGuid, cx, cy, nx, ny) =>
        dispatch('events.collisionEnter', otherGuid, { contactX: cx, contactY: cy, normalX: nx, normalY: ny }),
      onCollisionStay: (otherGuid, cx, cy, nx, ny) =>
        dispatch('events.collisionStay', otherGuid, { contactX: cx, contactY: cy, normalX: nx, normalY: ny }),
      onCollisionExit: (otherGuid) => dispatch('events.collisionExit', otherGuid, {}),
      onTriggerEnter: (otherGuid) => dispatch('events.triggerEnter', otherGuid, {}),
      onTriggerStay: (otherGuid) => dispatch('events.triggerStay', otherGuid, {}),
      onTriggerExit: (otherGuid) => dispatch('events.triggerExit', otherGuid, {}),
    };

    this._engine.setEventHandler(bodyHandle, handlers);
  }

  // ==========================================================================
  // INTERNAL — zones
  // ==========================================================================

  private _registerGravityZone(go: GameObject): void {
    const d = go.getComponentData('core.GravityZone2D');
    if (!d) return;

    const desc: GravityZoneDescriptor = {
      guid: go.guid,
      position: { x: go.transform.position.x, y: go.transform.position.y },
      rotation: go.transform.localRotationRadians,
      scale: { x: go.transform.localScale.x, y: go.transform.localScale.y },
      shape: d.shape,
      type: d.zoneType,
      gravity: d.gravity,
      strength: d.strength,
      falloff: d.falloff,
      innerRadius: d.innerRadius,
      outerRadius: d.outerRadius,
      size: d.size,
      direction: d.direction,
      points: d.points,
      priority: d.priority,
      affectedLayers: d.affectedLayers,
    };

    this._engine.addGravityZone(desc);
  }

  private _registerBuoyancyZone(go: GameObject): void {
    const d = go.getComponentData('core.BuoyancyZone2D');
    if (!d) return;

    const desc: BuoyancyZoneDescriptor = {
      guid: go.guid,
      position: { x: go.transform.position.x, y: go.transform.position.y },
      rotation: go.transform.localRotationRadians,
      scale: { x: go.transform.localScale.x, y: go.transform.localScale.y },
      density: d.density,
      linearDrag: d.linearDrag,
      angularDrag: d.angularDrag,
      flow: d.flow,
      surfaceNormal: d.surfaceNormal,
      width: d.width,
      height: d.height,
      priority: d.priority,
      affectedLayers: d.affectedLayers,
    };

    this._engine.addBuoyancyZone(desc);
  }

  private _registerWindZone(go: GameObject): void {
    const d = go.getComponentData('core.WindZone2D');
    if (!d) return;

    const desc: WindZoneDescriptor = {
      guid: go.guid,
      position: { x: go.transform.position.x, y: go.transform.position.y },
      rotation: go.transform.localRotationRadians,
      scale: { x: go.transform.localScale.x, y: go.transform.localScale.y },
      mode: d.mode,
      direction: d.direction,
      strength: d.strength,
      turbulence: d.turbulence,
      turbulenceFrequency: d.turbulenceFrequency,
      pulse: d.pulse,
      pulseFrequency: d.pulseFrequency,
      shape: d.shape,
      radius: d.radius,
      width: d.width,
      height: d.height,
      falloff: d.falloff,
      falloffStart: d.falloffStart,
      priority: d.priority,
      affectedLayers: d.affectedLayers,
    };

    this._engine.addWindZone(desc);
  }

  // ==========================================================================
  // INTERNAL — physics → scene
  // ==========================================================================

  private _syncBodiesToTransforms(): void {
    if (!this._level) return;

    this._engine.forEachBody((body) => {
      if (body.bodyType === PhysicsBodyType.Static) return;

      const go = this._level!.findByGuid(body.guid);
      if (!go) return;

      go.transform.setLocalPosition(body.position.x, body.position.y);
      go.transform.localRotationRadians = -body.rotation;
      go.transform.markDirty();
    });
  }
}
